import { useLocation, useSearchParams } from 'react-router';

import { ReactComponent as FiltersIcon } from '@rocco/icons/svg/filters.svg';
import { cn } from '@rocco/utils/cn';
import type { useSearchFilters } from '@modules/search/hook/use-search-filters';
import { useFamilyBrandFilter } from '@modules/professional/hook/use-family-brand-filter';
import { CategoryPills } from '~/ui-rocco/pages/category-page/components/CategoryPills';
import { ProfessionalBrandFamilyDropdown } from './ProfessionalBrandFamilyDropdown';

/** The category filter's URL param — `category` maps to `categoryLink`. */
const CATEGORY_PARAM = 'categoryLink';

/** Shared pill metrics (Figma 5526:100315): 42px tall, 21px sides, 16px type. */
const PILL =
    'flex h-10.5 shrink-0 items-center justify-center rounded-full text-base whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-hidden';

/** Vertical rule grouping the bar's three clusters. */
const Divider = ({ className }: { className?: string }) => (
    <span
        aria-hidden="true"
        className={cn('h-[2.098rem] w-px shrink-0 bg-black/15', className)}
    />
);

/**
 * Quick-filter bar above the Products grid (Figma 5526:100315) — the filter
 * sidebar toggle, the brand-family dropdown, then the top-level product
 * categories as pills.
 *
 * The brand dropdown is what pulls sister-brand products into the tab: the grid
 * shows this profile's own products until a visitor ticks a sibling. The same
 * selection also renders as a named group in the filter drawer — both read
 * {@link useFamilyBrandFilter}, so the two can't disagree about what is on.
 */
export const ProfessionalProductsFilterBar = ({
    searchFilters,
    filterDrawerOpen,
    toggleFilterDrawer,
}: {
    searchFilters: ReturnType<typeof useSearchFilters>;
    filterDrawerOpen: boolean;
    toggleFilterDrawer: () => void;
}) => {
    const brandChips = useFamilyBrandFilter();

    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();

    // Always the root's children, so the pills never shift under the visitor
    // mid-drill; the sidebar owns the deeper tree.
    const categoryState = searchFilters.filterStates?.category;
    const root = categoryState?.category;
    const selectedId = categoryState?.selectedCategory?.id;
    const categoryLink = (id?: string) => {
        const params = new URLSearchParams(searchParams);
        if (id) params.set(CATEGORY_PARAM, id);
        else params.delete(CATEGORY_PARAM);
        const query = params.toString();
        return query ? `${pathname}?${query}` : pathname;
    };
    const categories = root
        ? [
              { id: 0, title: 'All', link: categoryLink() },
              ...(root.children ?? []).map((child, index) => ({
                  id: index + 1,
                  title: child.name,
                  link: categoryLink(child.id),
                  segment: child.id,
              })),
          ]
        : [];
    const activeIndex = categories.findIndex(
        (category) => 'segment' in category && category.segment === selectedId,
    );

    if (brandChips.length === 0 && categories.length === 0) return null;

    return (
        // Filter toggle, brand dropdown and category pills share one row — the
        // dropdown is one pill wide however large the family is, so only an
        // unusually long category list can crowd the row now. The wrap and the
        // pills' min-width below are the fallback for that. Mobile always stacks.
        <div className="flex gap-3 max-md:flex-col md:flex-wrap md:items-center">
            <div className="flex min-w-0 shrink-0 items-center gap-3">
                <button
                    type="button"
                    onClick={toggleFilterDrawer}
                    aria-expanded={filterDrawerOpen}
                    className={cn(
                        PILL,
                        // Mobile keeps the filter sheet on SearchFilter's own
                        // Refine button, so this pill is desktop-only.
                        'gap-2 bg-[#181818] pr-5.25 pl-[0.881rem] text-white hover:bg-[#2a2a2a] max-md:hidden',
                    )}
                >
                    {/* The svg's path carries no `fill`, so it defaults to black
                        and disappears on the dark pill — it needs `fill-*`, not
                        `text-*`. */}
                    <FiltersIcon className="size-[0.881rem] fill-white" />
                    {filterDrawerOpen ? 'Hide Filters' : 'Show Filters'}
                </button>
                {brandChips.length > 0 ? (
                    <>
                        <Divider className="max-md:hidden" />
                        <ProfessionalBrandFamilyDropdown brands={brandChips} />
                    </>
                ) : null}
            </div>
            {categories.length > 0 ? (
                // The min-width is what makes the row wrap instead of squeezing
                // the pills down to nothing when the chips are wide.
                <div className="flex min-w-0 flex-1 items-center gap-3 md:min-w-[18rem]">
                    <Divider className="max-md:hidden" />
                    {/* CategoryPills carries its own 12px scroller inset; cancel
                        it so the first pill sits 12px off the divider on desktop
                        and flush with the brand chips' gutter on mobile. */}
                    <div className="-ml-3 min-w-0 flex-1">
                        <CategoryPills
                            categories={categories}
                            activeId={activeIndex === -1 ? 0 : activeIndex}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
};
